// Copyright 2026 Bitech.id. Licensed under the MIT License.

import type { BiwaveTarget } from '../types.js';

const MAX_RETRIES = 3;
const MAX_QUEUE_SIZE = 30;
const BASE_RETRY_DELAY_MS = 1000;

interface QueueItem {
  id: string;
  envelope: string;
}

export class Transport {
  private readonly queue: QueueItem[] = [];
  private sequence = 0;

  constructor(private readonly target: BiwaveTarget) {}

  send(envelope: string): void {
    if (this.queue.length >= MAX_QUEUE_SIZE) {
      console.warn('[Biwave] transport queue is full, dropping event');
      return;
    }

    const item: QueueItem = { id: `${++this.sequence}`, envelope };
    this.queue.push(item);

    void this.attempt(item, 0);
  }

  get pendingCount(): number {
    return this.queue.length;
  }

  /**
   * Single-attempt send with no internal queue/retry — the caller (e.g.
   * LogBuffer) owns retry/backoff for this call. Used for anything that
   * needs to know whether the send actually succeeded, unlike send()'s
   * fire-and-forget queue.
   */
  async sendNow(
    body: string,
    url: string = this.target.ingestUrl,
    contentType = 'application/x-ndjson',
  ): Promise<void> {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': contentType,
        'X-Biwave-Client-Id': this.target.clientId,
      },
      body,
    });

    if (!response.ok) {
      throw new Error(`Biwave ingest responded with HTTP ${response.status}`);
    }
  }

  private async attempt(item: QueueItem, retryCount: number): Promise<void> {
    try {
      await this.sendNow(item.envelope);
      this.dequeue(item);
    } catch (err) {
      if (retryCount >= MAX_RETRIES) {
        this.dequeue(item);
        console.warn('[Biwave] failed to send event after retries, dropping it', err);
        return;
      }

      const delay = BASE_RETRY_DELAY_MS * 2 ** retryCount;
      setTimeout(() => {
        void this.attempt(item, retryCount + 1);
      }, delay);
    }
  }

  private dequeue(item: QueueItem): void {
    const index = this.queue.indexOf(item);
    if (index !== -1) {
      this.queue.splice(index, 1);
    }
  }
}
